// @flow

import {useMemo, useState, useCallback, useEffect} from 'react';
import {useTypedSelector} from '../../../store';
import {useActions} from '../../../utils/globalStore';

import {useIsFeatureInTier} from '../../../utils/component';

import {actions as OrderActions} from '../duck';

import type {
  Order,
  OrderTaxInvoiceInformation,
  OrderTaxInvoice,
  TaxInvoiceStateType,
  OrderState,
} from '../../../entities';

import type {RootState} from '../..';
import {
  cancelOrderTaxInvoiceService,
  getTaxInvoiceOrdersByIdsService,
  generateOrderTaxInvoiceService,
  getTaxInvoiceDocumentsService,
} from '../../tax-invoice/promises';
import {useOrdersActions} from '../../orders/utils';

import type {OrderTaxInvoiceData} from '../../../entities';

export function formatDateToCustomFormat(inputDateStr: string) {
  const dateParts = inputDateStr.split('T')[0].split('-');
  const year = dateParts[0];
  const month = dateParts[1];
  const day = dateParts[2];
  return `${day}/${month}/${year}`;
}

const TAX_INVOICE_PENDING_STATES: TaxInvoiceStateType[] = ['PENDING'];

export function canGoPrevOrderState(
  order: Order,
  orderTaxInvoice?: OrderTaxInvoice
) {
  if (!orderTaxInvoice) return true;
  const {orderState} = order;
  if (orderState !== 'PAID') return true;

  const taxInvoiceList = orderTaxInvoice?.taxInvoiceList;

  if (!taxInvoiceList) return true;

  const hasSomeTaxInvoiceInProgress = taxInvoiceList.some(item => {
    return TAX_INVOICE_PENDING_STATES.includes(item.state);
  });

  if (hasSomeTaxInvoiceInProgress) return false;

  const taxInvoiceAccepted = taxInvoiceList
    .filter(
      item =>
        item.taxInvoiceType.name === 'BOLETA' ||
        item.taxInvoiceType.name === 'FACTURA'
    )
    .some(item => item.state === 'ACCEPTED');

  if (!taxInvoiceAccepted) return true;

  const hasSuccessCreditNote = taxInvoiceList.some(
    item =>
      item.state === 'ACCEPTED' && item.taxInvoiceType.name === 'NOTA_CREDITO'
  );

  return taxInvoiceAccepted && hasSuccessCreditNote;
}

const statesWithRollbackRestriction: OrderState[] = [
  'PAID',
  'ON_THE_WAY',
  'DELIVERED',
];
const prePaymentStates: OrderState[] = ['PENDING', 'CONFIRMED'];

export function getAvailableOrderStates(
  order: Order,
  orderTaxInvoice?: OrderTaxInvoice
): {
  statesToProceed: OrderState[],
  statesToRevert: OrderState[],
} {
  const originalAvailableStates = {
    statesToProceed: order.availableOrderStatesToProceed,
    statesToRevert: order.availableOrderStatesToRevert,
  };
  if (!orderTaxInvoice) {
    return originalAvailableStates;
  }

  const {orderState} = order;
  const taxInvoiceList = orderTaxInvoice.taxInvoiceList || [];

  if (!statesWithRollbackRestriction.includes(orderState)) {
    return originalAvailableStates;
  }

  const hasSomeTaxInvoiceInProgress = taxInvoiceList.some(item => {
    return TAX_INVOICE_PENDING_STATES.includes(item.state);
  });

  if (hasSomeTaxInvoiceInProgress) {
    return {
      statesToProceed: order.availableOrderStatesToProceed,
      statesToRevert: order.availableOrderStatesToRevert.filter(
        state => !prePaymentStates.includes(state)
      ),
    };
  }

  const taxInvoiceAccepted = taxInvoiceList
    .filter(
      item =>
        item.taxInvoiceType.name === 'BOLETA' ||
        item.taxInvoiceType.name === 'FACTURA'
    )
    .some(item => item.state === 'ACCEPTED');

  if (!taxInvoiceAccepted) {
    return originalAvailableStates;
  }

  const hasSuccessCreditNote = taxInvoiceList.some(
    item =>
      item.state === 'ACCEPTED' && item.taxInvoiceType.name === 'NOTA_CREDITO'
  );

  if (taxInvoiceAccepted && hasSuccessCreditNote) {
    return originalAvailableStates;
  } else {
    return {
      statesToProceed: order.availableOrderStatesToProceed,
      statesToRevert: order.availableOrderStatesToRevert.filter(
        state => !prePaymentStates.includes(state)
      ),
    };
  }
}

export const useOrder = () => {
  const order = useTypedSelector(state => state.order.order);
  return order;
};

export const useOrderItemsTaxes = (order?: Order, isOrder: boolean = true) => {
  const hasTaxesFeature = useIsFeatureInTier('TAXES');
  const orderItemTaxesMap = useMemo(() => {
    if (!hasTaxesFeature) return {};
    const objMap = {};
    const taxEval = isOrder ? order?.invoice?.taxes : order?.taxes;
    if (taxEval) {
      for (let tax of taxEval) {
        const taxItemsId = tax.items;
        for (let item of taxItemsId) {
          const prevValue = objMap[item] ?? 1;
          objMap[item] = prevValue + parseFloat(tax.percentage);
        }
      }
    }
    return objMap;
  }, [hasTaxesFeature, isOrder, order]);

  return orderItemTaxesMap;
};

const currentOrderSelector = (state: RootState) => state.order.order;
const currentOrderTaxInvoiceSelector = (state: RootState) =>
  state.order.orderTaxInvoice;

export const useCurrentOrder = () => {
  const order = useTypedSelector(currentOrderSelector);
  const orderTaxInvoice = useTypedSelector(currentOrderTaxInvoiceSelector);
  return {
    order,
    taxInvoiceData: orderTaxInvoice,
  };
};

export function useCurrentOrderActions() {
  const setOrderTaxInvoiceAction = useActions(OrderActions.setOrderTaxInvoice);

  const [loading, setLoading] = useState(false);

  const {updateOrderTaxInvoice} = useOrdersActions();

  const updateOrderTaxInvoiceInformation = (
    information: OrderTaxInvoiceInformation
  ) => {
    setOrderTaxInvoiceAction({
      taxInvoiceInformation: information,
    });
  };

  const cancelOrderTaxInvoice = async (
    orderId: string,
    orderTaxInvoiceId: string
  ) => {
    setLoading(true);
    try {
      await cancelOrderTaxInvoiceService(orderTaxInvoiceId);
      const taxInvoices = await getTaxInvoiceOrdersByIdsService([orderId]);
      const taxInvoice = taxInvoices && taxInvoices[0];
      if (!taxInvoice) return;
      updateOrderTaxInvoice(orderId, taxInvoice.orderTaxInvoices);
      setOrderTaxInvoiceAction({
        taxInvoiceList: taxInvoice.orderTaxInvoices,
      });
    } finally {
      setLoading(false);
    }
  };

  const emitOrderTaxInvoice = async (orderId: string) => {
    setLoading(true);
    try {
      await generateOrderTaxInvoiceService(orderId);
      const taxInvoices = await getTaxInvoiceOrdersByIdsService([orderId]);
      const taxInvoice = taxInvoices && taxInvoices[0];
      if (!taxInvoice) return;
      updateOrderTaxInvoice(orderId, taxInvoice.orderTaxInvoices);
      setOrderTaxInvoiceAction({
        taxInvoiceList: taxInvoice.orderTaxInvoices,
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshOrderTaxInvoice = useCallback(
    async (orderId: string) => {
      const taxInvoices = await getTaxInvoiceOrdersByIdsService([orderId]);
      const taxInvoice = taxInvoices && taxInvoices[0];
      if (!taxInvoice) return;
      updateOrderTaxInvoice(orderId, taxInvoice.orderTaxInvoices);
      setOrderTaxInvoiceAction({
        taxInvoiceList: taxInvoice.orderTaxInvoices,
      });
    },
    [setOrderTaxInvoiceAction, updateOrderTaxInvoice]
  );

  return {
    loading,
    emitOrderTaxInvoice,
    cancelOrderTaxInvoice,
    refreshOrderTaxInvoice,
    updateOrderTaxInvoiceInformation,
  };
}

const MIN_LOADING_DURATION_MS = 500;

type UseOrderTaxInvoiceReturn = {
  loading: boolean,
  initialLoading: boolean,
  taxInvoiceInformation: OrderTaxInvoiceInformation | null,
  taxInvoiceList: OrderTaxInvoiceData[],
  fetchTaxInvoiceData: (orderId: string) => Promise<void>,
  refetch: () => Promise<void>,
};

export function useOrderTaxInvoice(orderId?: string): UseOrderTaxInvoiceReturn {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [
    taxInvoiceInformation,
    setTaxInvoiceInformation,
  ] = useState<OrderTaxInvoiceInformation | null>(null);
  const [taxInvoiceList, setTaxInvoiceList] = useState<OrderTaxInvoiceData[]>(
    []
  );

  const setOrderTaxInvoiceAction = useActions(OrderActions.setOrderTaxInvoice);
  const {updateOrderTaxInvoice} = useOrdersActions();

  const fetchTaxInvoiceData = useCallback(
    async (orderIdToFetch: string) => {
      const startTime = Date.now();
      setLoading(true);

      try {
        const taxInfoResponse = await getTaxInvoiceDocumentsService(
          orderIdToFetch
        );

        const orderTaxInvoiceInformation: OrderTaxInvoiceInformation = {
          taxReceiverCode: taxInfoResponse.taxReceiverCode,
          taxReceiverName: taxInfoResponse.taxReceiverName,
          id: taxInfoResponse.id,
          taxInvoiceType: taxInfoResponse.taxInvoiceType,
          address: taxInfoResponse.address,
          admin1: taxInfoResponse.admin1,
          admin2: taxInfoResponse.admin2,
          admin3: taxInfoResponse.admin3,
        };

        setTaxInvoiceInformation(orderTaxInvoiceInformation);

        const taxInvoices = await getTaxInvoiceOrdersByIdsService([
          orderIdToFetch,
        ]);
        const taxInvoice = taxInvoices && taxInvoices[0];

        if (taxInvoice) {
          setTaxInvoiceList(taxInvoice.orderTaxInvoices || []);
          updateOrderTaxInvoice(orderIdToFetch, taxInvoice.orderTaxInvoices);
          setOrderTaxInvoiceAction({
            taxInvoiceList: taxInvoice.orderTaxInvoices,
            taxInvoiceInformation: orderTaxInvoiceInformation,
          });
        } else {
          setTaxInvoiceList([]);
          setOrderTaxInvoiceAction({
            taxInvoiceInformation: orderTaxInvoiceInformation,
          });
        }
      } catch (error) {
        console.warn('Error fetching tax invoice data:', error);
        setTaxInvoiceInformation(null);
        setTaxInvoiceList([]);
      } finally {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = MIN_LOADING_DURATION_MS - elapsedTime;

        if (remainingTime > 0) {
          setTimeout(() => {
            setLoading(false);
            setInitialLoading(false);
          }, remainingTime);
        } else {
          setLoading(false);
          setInitialLoading(false);
        }
      }
    },
    [setOrderTaxInvoiceAction, updateOrderTaxInvoice]
  );

  const refetch = useCallback(async () => {
    if (orderId) {
      await fetchTaxInvoiceData(orderId);
    }
  }, [orderId, fetchTaxInvoiceData]);

  useEffect(() => {
    if (orderId) {
      fetchTaxInvoiceData(orderId);
    }
  }, [orderId, fetchTaxInvoiceData]);

  return {
    loading,
    initialLoading,
    taxInvoiceInformation,
    taxInvoiceList,
    fetchTaxInvoiceData,
    refetch,
  };
}
